/** ocrResult에서 테이블과 동일한 소스의 행 배열 추출 */
export const extractOcrDataRows = (ocrResult: unknown): Record<string, unknown>[] => {
  if (!ocrResult) return [];
  const data = ocrResult as Record<string, unknown>;
  if (Array.isArray(data)) return data.filter(Boolean) as Record<string, unknown>[];
  if (Array.isArray(data?.entities)) return data.entities.filter(Boolean) as Record<string, unknown>[];
  if (Array.isArray(data?.items)) return data.items.filter(Boolean) as Record<string, unknown>[];
  if (Array.isArray(data?.data)) return data.data.filter(Boolean) as Record<string, unknown>[];
  if (Array.isArray(data?.rows)) return data.rows.filter(Boolean) as Record<string, unknown>[];
  const nested = data?.data as Record<string, unknown> | undefined;
  if (nested && typeof nested === 'object' && Array.isArray(nested.rows)) {
    return nested.rows.filter(Boolean) as Record<string, unknown>[];
  }
  return [];
};

type RowMapper = (row: Record<string, unknown>, index: number) => Record<string, unknown>;

const mapRowsContainer = (ocrResult: unknown, mapFn: RowMapper): unknown => {
  if (!ocrResult) return ocrResult;
  if (Array.isArray(ocrResult)) {
    return ocrResult.map((r, i) => mapFn(r as Record<string, unknown>, i));
  }
  if (ocrResult && typeof ocrResult === 'object') {
    const obj = ocrResult as Record<string, unknown>;
    if (Array.isArray(obj.rows)) {
      return { ...obj, rows: obj.rows.map((r, i) => mapFn(r as Record<string, unknown>, i)) };
    }
    const nested = obj.data as Record<string, unknown> | undefined;
    if (nested && typeof nested === 'object' && Array.isArray(nested.rows)) {
      return {
        ...obj,
        data: { ...nested, rows: nested.rows.map((r, i) => mapFn(r as Record<string, unknown>, i)) },
      };
    }
    if (Array.isArray(obj.entities)) {
      return { ...obj, entities: obj.entities.map((r, i) => mapFn(r as Record<string, unknown>, i)) };
    }
    if (Array.isArray(obj.items)) {
      return { ...obj, items: obj.items.map((r, i) => mapFn(r as Record<string, unknown>, i)) };
    }
    if (Array.isArray(obj.data)) {
      return { ...obj, data: obj.data.map((r, i) => mapFn(r as Record<string, unknown>, i)) };
    }
  }
  return ocrResult;
};

export const ensureDemoOcrRowKeys = (ocrResult: unknown, scopeId: string | number): unknown => {
  const scope = String(scopeId ?? 'scope');
  return mapRowsContainer(ocrResult, (row, idx) => {
    if (!row || typeof row !== 'object') return row;
    if (row.__demoRowKey) return row;
    const stableKey = `${String(row?.id ?? '').trim() || 'row'}::${scope}::${idx}`;
    return { ...row, __demoRowKey: stableKey };
  });
};

export const pickFirst = (obj: Record<string, unknown> | null | undefined, keys: string[]) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

export const updateOcrRows = (
  ocrResult: unknown,
  transformRows: (rows: Record<string, unknown>[]) => Record<string, unknown>[],
): unknown => {
  const applyTo = (rows: Record<string, unknown>[]) => {
    const nextRows = transformRows(Array.isArray(rows) ? rows : []);
    return Array.isArray(nextRows) ? nextRows : rows;
  };

  if (Array.isArray(ocrResult)) return applyTo(ocrResult as Record<string, unknown>[]);
  if (ocrResult && typeof ocrResult === 'object') {
    const obj = ocrResult as Record<string, unknown>;
    if (Array.isArray(obj.rows)) return { ...obj, rows: applyTo(obj.rows as Record<string, unknown>[]) };
    const nested = obj.data as Record<string, unknown> | undefined;
    if (nested && typeof nested === 'object' && Array.isArray(nested.rows)) {
      return { ...obj, data: { ...nested, rows: applyTo(nested.rows as Record<string, unknown>[]) } };
    }
    if (Array.isArray(obj.entities)) return { ...obj, entities: applyTo(obj.entities as Record<string, unknown>[]) };
    if (Array.isArray(obj.items)) return { ...obj, items: applyTo(obj.items as Record<string, unknown>[]) };
    if (Array.isArray(obj.data)) return { ...obj, data: applyTo(obj.data as Record<string, unknown>[]) };
  }
  return ocrResult;
};

export const patchOcrResultRows = (
  ocrResult: unknown,
  rowKey: string,
  patchFn: (row: Record<string, unknown>) => Record<string, unknown>,
): unknown => {
  const mapRow = (r: Record<string, unknown>, idx: number) => {
    const rk = String(r?.key ?? r?.id ?? idx);
    if (r?.key === rowKey || rk === rowKey || r?.id === rowKey || r?.__demoRowKey === rowKey) {
      return patchFn({ ...r });
    }
    return r;
  };
  return mapRowsContainer(ocrResult, mapRow);
};

export const replaceOcrResultRows = (
  cur: unknown,
  nextRows: Record<string, unknown>[],
): unknown => {
  if (Array.isArray(cur)) return nextRows;
  if (!cur || typeof cur !== 'object') return { items: nextRows };
  const obj = cur as Record<string, unknown>;
  if (Array.isArray(obj.rows)) return { ...obj, rows: nextRows };
  const nested = obj.data as Record<string, unknown> | undefined;
  if (nested && typeof nested === 'object' && Array.isArray(nested.rows)) {
    return { ...obj, data: { ...nested, rows: nextRows } };
  }
  if (Array.isArray(obj.items)) return { ...obj, items: nextRows };
  if (Array.isArray(obj.entities)) return { ...obj, entities: nextRows };
  if (Array.isArray(obj.data)) return { ...obj, data: nextRows };
  return { ...obj, items: nextRows };
};
